// /pricing — the voice and AI band.
//
// WHY THIS EXISTS. Before it, the word "voice" did not appear once on /pricing. Neither did
// "dictation", "speak", "transcribe" or "meeting notes" — verified by grep over the page. The
// flagship capability of the product had exactly two traces on the page where the buying decision
// is made: two auto-generated rows in a comparison table, deep below the fold. Meanwhile the
// SAME capability had a homepage band, a dedicated /modules page, a product film and a 33-clip
// audio library. The story was told everywhere except where it had to earn money.
//
// WHAT IT MAY CLAIM. The prose is the CMS `voice_ai` block — already published, already
// byte-mirrored to core/marketing.py DEFAULTS, already cleared against docs/marketing/
// CLAIMS_REGISTRY (rows A0 languages, A11 detection + refine, A12 meeting notes). Reusing it
// means this section ships NO new claim. What is new here is the SPECIFICITY the founder asked
// for: every number below is served from `pricing_integrity.catalogue`, computed at request time
// from the shipping code (sarvam_voice.TTS_LANGUAGES, meeting_notes._KINDS, ai_actions.ACTIONS,
// starter_rules.STARTER_RULES), so a figure on this page cannot drift from the product.
//
// THREE THINGS THIS SECTION MUST NEVER SAY, each a real trap:
//   1. It must not name a competitor product or claim equivalence with one. The audio clips are
//      synthetic and cannot back a quality comparison (CLAUDE.md). Describe the SHAPE of the
//      capability, never the comparison.
//   2. It must not say Aira knows who said what. Speaker labelling is MANUAL — the product does
//      not infer speaker identity, and the product film already ships that negative disclosure.
//   3. It must not say the redaction funnel covers voice. Voice/audio is one of the paths the
//      I-08 funnel does NOT reach (core/llm/egress_policy NON_FUNNEL_EGRESS). The approved card
//      copy is scoped correctly — "before any text reaches an AI model" — and must stay that way.
import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Mic, ShieldCheck, Sparkles, Waves } from "lucide-react";
import { cn } from "../../lib/utils";

// Places in the product that take a spoken input. Frontend-derived, so it is a constant here and
// `PricingVoiceAi.counts.test.js` RE-DERIVES it from the tree on every run — the same discipline
// as the module counts, which existed as a typed "23" against a live 28 until it was caught.
//
// THE NUMBER IS 72, AND TWO INDEPENDENT COUNTS SAID 70 FIRST. Both used
// `grep ... | grep -v -i test` to drop test files, which also drops any MOUNT LINE CARRYING
// `data-testid` — two of them do. The shared components also render a mic inside themselves
// (ui/VoiceFormAssist, ui/AiraNoteAssist and their two mobile twins); those four files are
// excluded because they are the component, not a surface. A line filter is not a file filter,
// and that is precisely why this lives in a test that walks the tree instead of in a shell
// pipeline nobody re-runs.
export const VOICE_SURFACES = 72;    // across 51 product files
export const VOICE_FORMS = 15;       // <VoiceFormAssist> — a whole form filled from one sentence
export const VOICE_NOTE_FIELDS = 47; // <AiraNoteAssist> — note/remark/comment fields
export const VOICE_AUDIO_CLIPS = 33; // public/marketing/voice-aira/audio/v1/manifest.json

const SURFACE = "bg-white/10 ring-1 ring-white/15";
// Measured on the band's DEEPEST end (#3B1478), which is the worst case, not its midpoint:
// white 13.49:1 · #DDD6FE 9.71:1. On the card fill: white 8.14:1 · #E9D5FF 5.98:1. All AA.
const SUB = "text-[#DDD6FE]";
const CARD_SUB = "text-[#E9D5FF]";

// Which plan first grants a feature, read off the LIVE comparison matrix rather than stated in
// copy — the gate line and the table below it can then never disagree, and a platform
// entitlement edit re-tiers this sentence with no deploy.
function firstPlanGranting(planMatrix, featureId) {
  const plans = planMatrix?.plans;
  if (!Array.isArray(plans)) return null;
  const hit = plans.find((p) => p?.features && p.features[featureId] && p.id !== "plan_trial");
  return hit?.name || null;
}

function Stat({ value, label }) {
  return (
    <div className={cn("rounded-2xl px-4 py-3.5 text-center", SURFACE)}>
      <div className="font-heading text-[26px] font-extrabold leading-none tracking-tight text-white tabular-nums">
        {value}
      </div>
      <div className={cn("mt-1.5 text-[11.5px] font-semibold leading-snug", SUB)}>{label}</div>
    </div>
  );
}

export default function PricingVoiceAi({ cms }) {
  const voiceCopy = cms?.voice_ai || {};
  const catalogue = cms?.pricing_integrity?.catalogue || null;
  const voice = catalogue?.voice || null;
  const ai = catalogue?.ai || null;
  const matrix = cms?.plan_matrix || null;

  // Every figure is served or omitted. A missing count drops its chip rather than rendering a
  // placeholder or a guess — an invented number on a pricing page is the one defect this whole
  // rebuild exists to remove.
  const languages = Array.isArray(voice?.languages) ? voice.languages : [];
  const indian = voice?.indian_language_count;
  const languageLabel = indian ? `English + ${indian} Indian languages` : null;

  const dictationPlan = firstPlanGranting(matrix, "assistant");
  const notesPlan = firstPlanGranting(matrix, "meeting_notes");

  const cards = Array.isArray(voiceCopy.cards) ? voiceCopy.cards.filter((c) => c?.title) : [];
  const CARD_ICONS = [Waves, Mic, Sparkles, ShieldCheck];

  const stats = [
    { value: VOICE_SURFACES, label: "places in the app take your voice" },
    languageLabel ? { value: voice.language_count, label: languageLabel } : null,
    { value: VOICE_FORMS, label: "forms fill from one spoken sentence" },
    voice?.dictation_styles ? { value: voice.dictation_styles, label: "writing styles, per field" } : null,
    voice?.note_kinds ? { value: voice.note_kinds, label: "kinds of meeting note" } : null,
    { value: "0", label: "audio files stored, ever" },
  ].filter(Boolean);

  return (
    <section
      className="relative scroll-mt-24 overflow-hidden bg-[linear-gradient(135deg,#6D28D9_0%,#4C1D95_55%,#3B1478_100%)]"
      aria-labelledby="pricing-voice-h"
      data-testid="pricing-voice-ai">
      <div className="mx-auto max-w-6xl px-5 py-14 sm:py-16">
        {voiceCopy.badge && (
          <p className="inline-flex items-center gap-1.5 rounded-full bg-white/12 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.15em] text-white ring-1 ring-white/20">
            <Mic className="h-3 w-3" aria-hidden="true" /> {voiceCopy.badge}
          </p>
        )}
        <h2 id="pricing-voice-h"
            className="mt-4 max-w-3xl font-heading text-[30px] font-bold leading-[1.14] tracking-tight text-white sm:text-[38px]">
          {voiceCopy.heading || "Speak to your school software —"}{" "}
          {voiceCopy.highlight && <span className="text-[#C4B5FD]">{voiceCopy.highlight}</span>}
        </h2>
        {voiceCopy.subhead && (
          <p className={cn("mt-4 max-w-3xl text-[16px] leading-relaxed", SUB)}>{voiceCopy.subhead}</p>
        )}

        {/* THE SPEC STRIP. The founder's note was that nobody reads a paragraph — so the claim is
            made in figures a buyer can check, not in prose. */}
        <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {stats.map((s) => <Stat key={s.label} value={s.value} label={s.label} />)}
        </div>

        {/* The four approved capability cards, verbatim from the published CMS block. */}
        {cards.length > 0 && (
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {cards.map((card, i) => {
              const Icon = CARD_ICONS[i % CARD_ICONS.length];
              return (
                <div key={card.title} className={cn("rounded-2xl p-5", SURFACE)}>
                  <span className="grid h-9 w-9 place-items-center rounded-xl bg-white/15 ring-1 ring-white/20">
                    <Icon className="h-[18px] w-[18px] text-white" strokeWidth={1.9} aria-hidden="true" />
                  </span>
                  <h3 className="mt-3.5 font-heading text-[15px] font-bold leading-snug text-white">
                    {card.title}
                  </h3>
                  <p className={cn("mt-2 text-[13px] leading-relaxed", CARD_SUB)}>{card.body}</p>
                </div>
              );
            })}
          </div>
        )}

        {/* THE LANGUAGE RIBBON. Served from sarvam_voice's own table, in the native script, so the
            list a school reads is the list the provider actually accepts. */}
        {languages.length > 0 && (
          <div className="mt-8">
            <ul className="flex flex-wrap gap-2" data-testid="pricing-voice-languages">
              {languages.map((l) => (
                <li key={l.code}
                    className={cn("inline-flex items-baseline gap-1.5 rounded-full px-3 py-1.5 text-[13px]", SURFACE)}>
                  <span className="font-semibold text-white">{l.native}</span>
                  {l.english !== l.native && (
                    <span className={cn("text-[11.5px]", SUB)}>{l.english}</span>
                  )}
                </li>
              ))}
            </ul>
            <p className={cn("mt-3 text-[13px]", SUB)}>
              Speak any of these. Aira writes the record in English.
            </p>
          </div>
        )}

        {/* WHERE AI ALREADY WORKS. Three counts, each one `len()` of a live registry — deliberately
            NOT a total "AI capabilities" figure, because the only total that exists is a hand-made
            audit tally and a number nothing can recompute goes stale in silence. */}
        {ai && (ai.actions || ai.staff_tools || ai.starter_rules) && (
          <div className={cn("mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 rounded-2xl px-5 py-4 text-[13.5px]", SURFACE, SUB)}
               data-testid="pricing-ai-facts">
            {ai.actions ? (
              <span><strong className="text-white tabular-nums">{ai.actions}</strong> actions Aira can take — it asks you before every one</span>
            ) : null}
            {ai.staff_tools ? (
              <span><strong className="text-white tabular-nums">{ai.staff_tools}</strong> live school-data tools, each permission-checked</span>
            ) : null}
            {ai.starter_rules ? (
              <span><strong className="text-white tabular-nums">{ai.starter_rules}</strong> ready-made automation rules</span>
            ) : null}
          </div>
        )}

        <div className="mt-8 flex flex-wrap items-center gap-4">
          {/* Arbitrary-value white, not the `bg-white` utility: index.css remaps `.bg-white` to the
              dark surface token in dark mode, which took this deliberate always-white pill (against
              the section's own violet ground) down to a serious color-contrast failure once the
              "white" turned navy behind the unchanged violet text (measured live on `/pricing@dark`,
              same fix already applied to the pricing cards' own white `btn`/`tag`). */}
          <Link to="/modules/voice-aira"
                className="inline-flex min-h-11 items-center gap-2 rounded-full bg-[#ffffff] px-5 text-[14px] font-bold text-[#4C1D95] transition-[background-color,transform] duration-fast ease-premium hover:bg-[#ffffff]/90 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#4C1D95]">
            Hear {VOICE_AUDIO_CLIPS} sample conversations
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
          {(dictationPlan || notesPlan) && (
            <p className={cn("text-[13px]", SUB)} data-testid="pricing-voice-gate">
              {dictationPlan ? `Dictation from ${dictationPlan}.` : ""}
              {notesPlan ? ` Meeting Notes from ${notesPlan}.` : ""}
            </p>
          )}
        </div>

        {/* The samples are synthetic. That disclosure travels WITH the offer to play them, never
            in a footnote three scrolls away. */}
        <p className={cn("mt-3 text-[12px]", SUB)}>
          Synthetic demonstration voices from fictional scripts — not customer recordings.
        </p>
        {voiceCopy.footnote && (
          <p className={cn("mt-2 max-w-3xl text-[12px] leading-relaxed", SUB)}>{voiceCopy.footnote}</p>
        )}
      </div>
    </section>
  );
}
